/**
 * Tracking context store — user, org, role, and current page (page_id, page_name).
 * Enables "which page was open" and "which action was taken on that page" in reports.
 */

import { create } from 'zustand'

export interface TrackingContext {
  user_id: string | null
  org_id: string | null
  role: string | null
  feature_name: string | null
  /** Stable id for the current screen (e.g. "orders", "order-details", "create-order") */
  page_id: string | null
  /** Human-readable screen name (e.g. "Orders List", "Order Details #123") */
  page_name: string | null
}

interface TrackingContextStore {
  context: TrackingContext
  setContext: (ctx: Partial<TrackingContext>) => void
  setFeatureFromPath: (pathname: string) => void
  /** Set current page so all actions are attributed to it. Call from route tracker or usePageTracking. */
  setPage: (page_id: string, page_name: string) => void
}

const pathToFeature: Record<string, string> = {
  '': 'dashboard',
  dashboard: 'dashboard',
  orders: 'orders',
  'create-order': 'order_creation',
  providers: 'providers',
  financials: 'financials',
  'account-settings': 'settings',
  users: 'user_management',
  notifications: 'notifications',
  login: 'auth',
  register: 'auth',
  'verify-otp': 'auth',
  'provider-registration': 'registration',
  'access-denied': 'auth',
}

function getFeatureFromPath(pathname: string): string {
  const segment = pathname.split('/').filter(Boolean)[0] || ''
  return pathToFeature[segment] ?? (segment || 'unknown')
}

export const useTrackingContextStore = create<TrackingContextStore>((set) => ({
  context: {
    user_id: null,
    org_id: null,
    role: null,
    feature_name: null,
    page_id: null,
    page_name: null,
  },

  setContext: (ctx) =>
    set((state) => ({
      context: { ...state.context, ...ctx },
    })),

  setFeatureFromPath: (pathname) =>
    set((state) => ({
      context: {
        ...state.context,
        feature_name: getFeatureFromPath(pathname),
      },
    })),

  setPage: (page_id, page_name) =>
    set((state) => ({
      context: {
        ...state.context,
        page_id,
        page_name,
      },
    })),
}))

/** Get current tracking context (for use outside React, e.g. in global listeners). */
export function getTrackingContext(): TrackingContext {
  return useTrackingContextStore.getState().context
}
